import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { 
  Bot, 
  Search, 
  Layers, 
  CheckCircle2, 
  ArrowRight, 
  Sparkles, 
  Cpu, 
  Share2,
  FileCheck
} from "lucide-react";

const HEO_PILLARS = [
  {
    num: "01",
    short: "01 Tech",
    title: "Technically Strong",
    badgeBg: "bg-[#4f46e5]/20 text-[#818cf8] group-hover:bg-[#4f46e5] group-hover:text-white",
    hoverBorder: "hover:border-[#6366f1]/50",
    activeBorder: "border-[#6366f1]/80 shadow-[0_0_15px_rgba(99,102,241,0.25)] bg-[#0e1424]",
    desc: "Make sure AI bots read our content cleanly by minimizing technical errors, adding proper Structured Data (Schema.org), and auditing AI crawler log files.",
    tag: "Schema & AI Log Audits",
    tagColor: "text-emerald-400",
    icon: CheckCircle2
  },
  {
    num: "02",
    short: "02 Intent",
    title: "Intent & Content",
    badgeBg: "bg-[#06b6d4]/20 text-[#38bdf8] group-hover:bg-[#06b6d4] group-hover:text-white",
    hoverBorder: "hover:border-[#06b6d4]/50",
    activeBorder: "border-[#06b6d4]/80 shadow-[0_0_15px_rgba(6,182,212,0.25)] bg-[#0e1424]",
    desc: "Find key questions asked by users from both internal and external sources, delivering actionable solutions with the capability to show ahead.",
    tag: "Internal & External Intent",
    tagColor: "text-cyan-300",
    icon: CheckCircle2
  },
  {
    num: "03",
    short: "03 Entity",
    title: "Entity",
    badgeBg: "bg-[#10b981]/20 text-[#34d399] group-hover:bg-[#10b981] group-hover:text-white",
    hoverBorder: "hover:border-[#10b981]/50",
    activeBorder: "border-[#10b981]/80 shadow-[0_0_15px_rgba(16,185,129,0.25)] bg-[#0e1424]",
    desc: "Improve the entity of our brand over the internet by maintaining consistent, authoritative information about the exact topics we want to lead.",
    tag: "Brand Topic Consistency",
    tagColor: "text-emerald-400",
    icon: CheckCircle2
  },
  {
    num: "04",
    short: "04 Measure",
    title: "Measurement",
    badgeBg: "bg-gradient-to-br from-[#4f46e5] to-[#06b6d4] text-white group-hover:scale-105",
    hoverBorder: "border-[#6366f1]/40 shadow-[0_0_20px_rgba(99,102,241,0.15)]",
    activeBorder: "border-[#6366f1]/80 shadow-[0_0_20px_rgba(99,102,241,0.3)] bg-[#0e1424]",
    desc: "Uncover the essential signals that help us understand which pages and citations are currently being surfaced and cited across LLM answer engines.",
    tag: "LLM Visibility Signals",
    tagColor: "text-[#38bdf8]",
    icon: Sparkles
  }
];

export default function HeoFrameworkVisualizer() {
  const [viewMode, setViewMode] = useState("heo"); // "traditional", "ai", "heo"
  const [activeHeoMobileStep, setActiveHeoMobileStep] = useState(0);
  const heoCarouselRef = useRef(null);

  const scrollToHeoMobileStep = (idx) => {
    setActiveHeoMobileStep(idx);
    const container = heoCarouselRef.current;
    if (container && container.children[idx]) {
      const child = container.children[idx];
      const targetLeft = child.offsetLeft - container.offsetLeft - (container.clientWidth - child.clientWidth) / 2;
      container.scrollTo({
        left: Math.max(0, targetLeft),
        behavior: "smooth"
      });
    }
  };

  const handleHeoCarouselScroll = () => {
    if (!heoCarouselRef.current) return;
    const { scrollLeft, offsetWidth } = heoCarouselRef.current;
    const newIdx = Math.round(scrollLeft / (offsetWidth * 0.82));
    if (newIdx >= 0 && newIdx < HEO_PILLARS.length && newIdx !== activeHeoMobileStep) {
      setActiveHeoMobileStep(newIdx);
    }
  };

  return (
    <section id="heo" className="py-7 lg:py-8 bg-[#080c14] border-y border-white/[0.08] relative overflow-hidden">
      {/* Background glow effects */}
      <div 
        className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[700px] h-[300px] pointer-events-none" 
        style={{ background: "radial-gradient(ellipse at center, rgba(79, 70, 229, 0.15) 0%, rgba(79, 70, 229, 0) 70%)" }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Section Heading */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-4 sm:mb-5"
        >
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#4f46e5]/15 border border-[#06b6d4]/30 text-xs font-mono font-bold uppercase tracking-wider text-[#38bdf8] mb-1.5 font-semibold">
            <Sparkles className="w-3.5 h-3.5 text-[#38bdf8]" />
            Proprietary Methodology
          </div>
          <h2 className="font-heading font-black text-2xl sm:text-3xl lg:text-[2.25rem] text-white tracking-tight uppercase mb-1.5 leading-tight">
            Hybrid Engine Optimization{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#818cf8] via-[#38bdf8] to-[#34d399]">
              (HEO Framework)
            </span>
          </h2>
          <p className="text-xs sm:text-sm text-white/75 max-w-2xl mx-auto leading-relaxed">
            Traditional SEO ranks web pages. Modern AI search synthesizes answers. 
            My unified HEO strategy ensures your B2B SaaS wins top organic rankings on Google while simultaneously being cited as the authoritative source by ChatGPT, Claude, and Google AI Overviews.
          </p>

          {/* Interactive Mode Switcher */}
          <div className="inline-flex items-center p-1.5 mt-3 rounded-xl bg-[#0d1322] border border-white/10 shadow-lg">
            <button
              onClick={() => setViewMode("traditional")}
              className={`px-3.5 py-1.5 rounded-lg text-xs sm:text-[13px] font-semibold transition-all ${
                viewMode === "traditional"
                  ? "bg-white/10 text-white shadow-sm"
                  : "text-white/60 hover:text-white"
              }`}
            >
              1. Traditional SEO
            </button>
            <button
              onClick={() => setViewMode("ai")}
              className={`px-3.5 py-1.5 rounded-lg text-xs sm:text-[13px] font-semibold transition-all ${
                viewMode === "ai"
                  ? "bg-white/10 text-white shadow-sm"
                  : "text-white/60 hover:text-white"
              }`}
            >
              2. AI Answer Engines
            </button>
            <button
              onClick={() => setViewMode("heo")}
              className={`px-4 py-1.5 rounded-lg text-xs sm:text-[13px] font-bold transition-all ${
                viewMode === "heo"
                  ? "btn-bold-primary text-white"
                  : "text-white/60 hover:text-white"
              }`}
            >
              3. The HEO Solution (Unified)
            </button>
          </div>
        </motion.div>

        {/* Visualizer Interactive Card */}
        <div className="max-w-6xl mx-auto rounded-2xl bg-[#0b101d] border border-white/10 p-4 sm:p-5 shadow-2xl relative">
          
          {/* Header Bar inside widget */}
          <div className="flex items-center justify-between pb-2.5 mb-3.5 border-b border-white/[0.08]">
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full bg-blue-500/80" />
              <div className="w-2.5 h-2.5 rounded-full bg-cyan-500/80" />
              <div className="w-2.5 h-2.5 rounded-full bg-emerald-500/80" />
              <span className="text-xs font-mono text-white/40 ml-1.5">HEO Search Architecture Simulator</span>
            </div>
            <span className="text-xs font-mono text-[#38bdf8] font-semibold uppercase tracking-wider">
              {viewMode === "traditional" && "Mode: Google Classic SERP (2015-2023)"}
              {viewMode === "ai" && "Mode: LLM Generative Synthesis (ChatGPT/Claude)"}
              {viewMode === "heo" && "Mode: Narendra's HEO Unified Strategy (Active in 2026)"}
            </span>
          </div>

          {/* Conditional Content depending on tab */}
          {viewMode === "traditional" && (
            <motion.div 
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center"
            >
              <div className="space-y-3">
                <span className="text-xs font-mono uppercase tracking-wider text-blue-400 font-bold">The Limitations of Classic SEO</span>
                <h3 className="font-heading font-bold text-xl sm:text-2xl text-white">Focus: Ranking in Top 3 of SERP Results</h3>
                <p className="text-xs sm:text-sm text-white/70 leading-relaxed">
                  Classic SEO strictly focuses on fighting to rank in the top 3 of SERP results through meta tags, keyword density, and backlink authority. 
                  However, when Google AI Overviews and LLMs summarize the query, top 3 organic listings are pushed below the fold and face zero-click drop-offs.
                </p>
                <div className="space-y-1.5 pt-1">
                  {[
                    "Targeting Top 3 SERP rank without AI answer optimization",
                    "Vulnerable to Google AI Overviews pushing #1 rankings down",
                    "Risk of zero-click drop-offs despite Top 3 positions",
                    "Disregards LLM RAG indexing pipelines"
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-white/60">
                      <span className="w-1.5 h-1.5 rounded-full bg-rose-400/80" />
                      {item}
                    </div>
                  ))}
                </div>
              </div>

              {/* SERP Mockup */}
              <div className="bg-[#0b0b0f] p-4.5 rounded-xl border border-white/10 space-y-2.5 font-sans">
                <div className="flex items-center gap-2 text-xs text-white/40 pb-1.5 border-b border-white/5">
                  <Search className="w-3.5 h-3.5" />
                  <span className="truncate">google.com/search?q=best+enterprise+sales+enablement+software</span>
                </div>
                <div className="p-3 rounded-lg bg-white/[0.03] border border-white/5">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-mono font-bold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded">
                      Rank #1 in SERP
                    </span>
                    <span className="text-xs text-blue-400 hover:underline cursor-pointer font-medium">
                      https://seismic.com › sales-enablement
                    </span>
                  </div>
                  <p className="text-sm text-blue-300 font-medium">Seismic: #1 Sales Enablement Software Platform</p>
                  <p className="text-xs text-white/50 line-clamp-2 mt-0.5">
                    Empower customer-facing teams with the right content, training, and buyer insights...
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-white/[0.01] border border-white/5 opacity-50">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-mono text-white/40 bg-white/[0.04] px-2 py-0.5 rounded">
                      Rank #2 in SERP
                    </span>
                    <span className="text-xs text-white/40">Competitor Organic Listing</span>
                  </div>
                  <p className="text-xs text-white/50">Alternative sales enablement solution comparison...</p>
                </div>
              </div>
            </motion.div>
          )}

          {viewMode === "ai" && (
            <motion.div 
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center"
            >
              <div className="space-y-3">
                <span className="text-xs font-mono uppercase tracking-wider text-purple-400 font-bold">The Rise of Generative Answer Engines</span>
                <h3 className="font-heading font-bold text-xl sm:text-2xl text-white">Focus: Direct LLM Synthesis & Citations</h3>
                <p className="text-xs sm:text-sm text-white/70 leading-relaxed">
                  Platforms like Claude, ChatGPT Search, and Gemini do not just display URLs. They read dozens of pages, extract consensus facts, and synthesize recommendations with embedded brand citations.
                </p>
                <div className="space-y-1.5 pt-1">
                  {["Requires entity authority & structured knowledge graphs", "Zero-click direct answering", "Requires high semantic density and clear definitions", "Cites brands with high third-party consensus"].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-white/60">
                      <span className="w-1.5 h-1.5 rounded-full bg-purple-400/80" />
                      {item}
                    </div>
                  ))}
                </div>
              </div>

              {/* AI Mockup */}
              <div className="bg-[#0b0b0f] p-4.5 rounded-xl border border-purple-500/20 space-y-2.5">
                <div className="flex items-center gap-2 text-xs text-purple-400 pb-1.5 border-b border-white/5 font-mono">
                  <Bot className="w-3.5 h-3.5" />
                  <span>Generative Synthesis (Claude & ChatGPT)</span>
                </div>
                <div className="p-3.5 rounded-lg bg-purple-950/20 border border-purple-500/30">
                  <p className="text-xs sm:text-sm text-white/90 leading-relaxed">
                    Based on enterprise review consensus and migration benchmarks, 
                    <strong className="text-white"> Seismic</strong> is consistently cited as the leading sales enablement platform for global teams <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/30 text-purple-300 font-mono">[1]</span>. Key differentiators include AI-driven content governance and deep CRM integrations <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/30 text-purple-300 font-mono">[2]</span>.
                  </p>
                </div>
                <div className="flex items-center gap-2 text-xs font-mono text-white/40">
                  <span>Sources: 4 verified entity citations indexed</span>
                </div>
              </div>
            </motion.div>
          )}

          {viewMode === "heo" && (
            <motion.div 
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-4 sm:space-y-5"
            >
              <div className="text-center max-w-2xl mx-auto">
                <span className="text-xs font-mono uppercase tracking-widest text-[#38bdf8] font-bold">The Full HEO Engine</span>
                <h3 className="font-heading font-black text-xl sm:text-2xl text-white mt-1">
                  How Narendra Bridges Both Worlds
                </h3>
                <p className="text-xs sm:text-sm text-white/70 mt-1 max-w-xl mx-auto">
                  The 4 core pillars of Hybrid Engine Optimization (HEO) that unify technical bot health, high-intent content, brand entity authority, and LLM measurement.
                </p>
              </div>

              {/* DESKTOP VIEW: 100% untouched original 4-card grid */}
              <div className="hidden lg:grid lg:grid-cols-4 gap-3.5">
                {/* Pillar 1: Technically Strong */}
                <motion.div 
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.05 }}
                  whileHover={{ y: -4, transition: { duration: 0.2 } }}
                  className="p-4 sm:p-4.5 rounded-xl bg-[#0e1424] border border-white/10 hover:border-[#6366f1]/50 transition-all flex flex-col justify-between group cursor-default"
                >
                  <div>
                    <div className="w-8 h-8 rounded-lg bg-[#4f46e5]/20 text-[#818cf8] flex items-center justify-center font-mono font-bold text-xs mb-2.5 group-hover:bg-[#4f46e5] group-hover:text-white transition-colors">
                      01
                    </div>
                    <h4 className="text-base font-bold text-white mb-1.5">Technically Strong</h4>
                    <p className="text-xs text-white/65 leading-relaxed mb-3">
                      Make sure AI bots read our content cleanly by minimizing technical errors, adding proper Structured Data (Schema.org), and auditing AI crawler log files.
                    </p>
                  </div>
                  <div className="pt-2.5 border-t border-white/[0.08] text-xs font-mono text-emerald-400 flex items-center gap-1.5 font-medium">
                    <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" /> Schema & AI Log Audits
                  </div>
                </motion.div>

                {/* Pillar 2: Intent & Content */}
                <motion.div 
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.1 }}
                  whileHover={{ y: -4, transition: { duration: 0.2 } }}
                  className="p-4 sm:p-4.5 rounded-xl bg-[#0e1424] border border-white/10 hover:border-[#06b6d4]/50 transition-all flex flex-col justify-between group cursor-default"
                >
                  <div>
                    <div className="w-8 h-8 rounded-lg bg-[#06b6d4]/20 text-[#38bdf8] flex items-center justify-center font-mono font-bold text-xs mb-2.5 group-hover:bg-[#06b6d4] group-hover:text-white transition-colors">
                      02
                    </div>
                    <h4 className="text-base font-bold text-white mb-1.5">Intent & Content</h4>
                    <p className="text-xs text-white/65 leading-relaxed mb-3">
                      Find key questions asked by users from both internal and external sources, delivering actionable solutions with the capability to show ahead.
                    </p>
                  </div>
                  <div className="pt-2.5 border-t border-white/[0.08] text-xs font-mono text-cyan-300 flex items-center gap-1.5 font-medium">
                    <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" /> Internal & External Intent
                  </div>
                </motion.div>

                {/* Pillar 3: Entity Authority */}
                <motion.div 
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.15 }}
                  whileHover={{ y: -4, transition: { duration: 0.2 } }}
                  className="p-4 sm:p-4.5 rounded-xl bg-[#0e1424] border border-white/10 hover:border-[#10b981]/50 transition-all flex flex-col justify-between group cursor-default"
                >
                  <div>
                    <div className="w-8 h-8 rounded-lg bg-[#10b981]/20 text-[#34d399] flex items-center justify-center font-mono font-bold text-xs mb-2.5 group-hover:bg-[#10b981] group-hover:text-white transition-colors">
                      03
                    </div>
                    <h4 className="text-base font-bold text-white mb-1.5">Entity</h4>
                    <p className="text-xs text-white/65 leading-relaxed mb-3">
                      Improve the entity of our brand over the internet by maintaining consistent, authoritative information about the exact topics we want to lead.
                    </p>
                  </div>
                  <div className="pt-2.5 border-t border-white/[0.08] text-xs font-mono text-emerald-400 flex items-center gap-1.5 font-medium">
                    <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" /> Brand Topic Consistency
                  </div>
                </motion.div>

                {/* Pillar 4: Measurement */}
                <motion.div 
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.2 }}
                  whileHover={{ y: -4, transition: { duration: 0.2 } }}
                  className="p-4 sm:p-4.5 rounded-xl bg-[#0e1424] border border-[#6366f1]/40 shadow-[0_0_20px_rgba(99,102,241,0.15)] flex flex-col justify-between group cursor-default"
                >
                  <div>
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#4f46e5] to-[#06b6d4] text-white flex items-center justify-center font-mono font-bold text-xs mb-2.5 group-hover:scale-105 transition-transform">
                      04
                    </div>
                    <h4 className="text-base font-bold text-white mb-1.5">Measurement</h4>
                    <p className="text-xs text-white/65 leading-relaxed mb-3">
                      Uncover the essential signals that help us understand which pages and citations are currently being surfaced and cited across LLM answer engines.
                    </p>
                  </div>
                  <div className="pt-2.5 border-t border-white/[0.08] text-xs font-mono text-[#38bdf8] flex items-center gap-1.5 font-medium">
                    <Sparkles className="w-3.5 h-3.5 flex-shrink-0" /> LLM Visibility Signals
                  </div>
                </motion.div>
              </div>

              {/* MOBILE VIEW (Option A): Step Selector Tabs + Horizontal Snap Carousel */}
              <div className="lg:hidden space-y-3">
                {/* 4 Step Selector Tabs in a single compact row */}
                <div className="flex items-center justify-between gap-1 p-1 rounded-xl bg-[#090e1a] border border-white/10">
                  {HEO_PILLARS.map((p, idx) => {
                    const isActive = activeHeoMobileStep === idx;
                    return (
                      <button
                        key={p.num}
                        onClick={() => scrollToHeoMobileStep(idx)}
                        className={`flex-1 py-1.5 px-1.5 rounded-lg text-[11px] sm:text-xs font-mono font-bold transition-all text-center ${
                          isActive
                            ? "bg-white/15 text-white shadow-sm border border-white/20"
                            : "text-white/50 hover:text-white"
                        }`}
                      >
                        {p.short}
                      </button>
                    );
                  })}
                </div>

                {/* Horizontal Snap-Scroll Track */}
                <div 
                  ref={heoCarouselRef}
                  onScroll={handleHeoCarouselScroll}
                  className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-1 no-scrollbar px-0.5"
                  style={{ WebkitOverflowScrolling: "touch" }}
                >
                  {HEO_PILLARS.map((pillar, idx) => {
                    const IconComponent = pillar.icon;
                    const isActive = activeHeoMobileStep === idx;
                    return (
                      <div 
                        key={pillar.num}
                        className={`w-[85vw] max-w-[310px] flex-shrink-0 snap-center p-4 rounded-xl border transition-all flex flex-col justify-between ${
                          isActive ? pillar.activeBorder : "bg-[#0e1424] border-white/10"
                        }`}
                      >
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-mono font-bold text-xs transition-colors ${pillar.badgeBg}`}>
                              {pillar.num}
                            </div>
                            <span className="text-[10px] font-mono text-white/40 uppercase tracking-widest">
                              Pillar {pillar.num} of 04
                            </span>
                          </div>
                          <h4 className="text-base font-bold text-white mb-1.5">{pillar.title}</h4>
                          <p className="text-xs text-white/65 leading-relaxed mb-3">
                            {pillar.desc}
                          </p>
                        </div>
                        <div className={`pt-2.5 border-t border-white/[0.08] text-xs font-mono ${pillar.tagColor} flex items-center gap-1.5 font-medium`}>
                          <IconComponent className="w-3.5 h-3.5 flex-shrink-0" /> {pillar.tag}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Dot Indicators & Swipe Hint */}
                <div className="flex items-center justify-between pt-0.5 px-1">
                  <div className="flex items-center gap-1.5">
                    {HEO_PILLARS.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => scrollToHeoMobileStep(i)}
                        className={`h-1.5 rounded-full transition-all ${
                          activeHeoMobileStep === i ? "w-5 bg-[#38bdf8]" : "w-1.5 bg-white/20"
                        }`}
                        aria-label={`Go to pillar ${i + 1}`}
                      />
                    ))}
                  </div>
                  <span className="text-[10px] font-mono text-white/40 flex items-center gap-1">
                    Swipe pillars ➔
                  </span>
                </div>
              </div>

              {/* Bottom Proof Note */}
              <div className="text-center py-2.5 px-4 rounded-xl bg-white/[0.02] border border-white/[0.06] flex items-center justify-center gap-3">
                <span className="font-handwriting text-xl sm:text-2xl text-amber-300 font-bold tracking-wide">
                  Implemented successfully at Seismic Software!
                </span>
              </div>
            </motion.div>
          )}

        </div>

      </div>
    </section>
  );
}
